/**
 * ============================================================
 * AdminPubs.js — Manage Publications (Admin Panel)
 * ============================================================
 *
 * Loads:
 *   - List of publications from backend (GET /api/publications)
 *
 * Features:
 *   - Search by publication title
 *   - Click a row to edit/delete a publication
 *   - "Add Project" button -> /admin/publications/new
 *
 * Navigation:
 *   - Uses SynlabAdminNavbar for consistent admin navigation
 *   - Uses shared AdminSubpage.css for layout
 **/

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import SearchBar from "../components/SearchBar";
import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminPubs() {

  const [publications, setPublications] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Load publications once token is confirmed valid
  // ------------------------------------------------------------
  useEffect(() => {
    async function fetchPublications() {
      try {
        const res = await authFetch("/api/publications");
        if (!res) return; // redirected on invalid token

        const data = await res.json();

        // Make sure data is an array
        if (Array.isArray(data)) {
          setPublications(data);
        } else {
          console.warn("Publications API returned non-array: ", data);
          setPublications([]); // fallback to empty list          
        }

      } catch (err) {
        console.error("AdminPublications fetch error:", err);
        setPublications([]); // fallback instead of crashing
      }
    }

    fetchPublications();
  }, []);

  // ------------------------------------------------------------
  // Filtered list of publications
  // ------------------------------------------------------------
  const filteredPubs = useMemo(() => {
    const s = search.toLowerCase();

    // Check that publications is an array
    const list = Array.isArray(publications) ? publications : [];

    return list.filter((p) =>
      (p.title || "").toLowerCase().includes(s)
    );
  }, [search, publications]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- PAGE HEADER ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">Manage Publications</h1>

          <Button
            className="admin-add-button"
            onClick={() => navigate("/admin/publications/new")}
          >
            + Add Publication
          </Button>
        </div>

        {/* ----- PAGE DESCRIPTION ----- */}
        <p className="admin-page-description">
          Search, view, and edit all Synlab publication records.
        </p>

        {/* ----- SEARCH BAR ----- */}
        <div className="admin-searchbar-wrapper">
          <SearchBar
            placeholder="Search by publication title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ----- PUBLICATIONS TABLE IS EMPTY ----- */}
        {publications.length === 0 && search === "" && (
          <div className="admin-empty-state">
            <p>No publications found.</p>
          </div>
        )}

        {/* ----- PUBLICATIONS TABLE ----- */}
        {publications.length > 0 && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Year</th>
                  <th>Type</th>
                </tr>
              </thead>

              <tbody>
                {(search ? filteredPubs : publications).map((pub) => (
                  <tr
                    key={pub.id}
                    className="admin-table-row"
                    onClick={() => navigate(`/admin/publications/${pub.id}`)}
                  >
                    <td>{pub.title}</td>
                    <td>{pub.year}</td>
                    <td>{pub.pubtype || "Not Published"}</td>
                  </tr>
                ))}

                {(search && filteredPubs.length === 0) && (
                  <tr>
                    <td colSpan="3">No publications match your search</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabAdminFooter />

    </div>
  );
}

export default AdminPubs;
